import React, { useCallback, useEffect, useState, useRef } from "react";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
	ChevronRight,
	ChevronDown,
	Menu as MenuIcon,
	X,
	Calendar,
	Bell,
	Smartphone,
	Globe,
	Gift,
	Star,
	Users,
	CheckCircle,
	Sparkles,
	MessageCircle,
	Mail,
	Phone,
	Instagram,
	ArrowRight,
	Shield,
	Zap,
	ShoppingBag,
} from "lucide-react";
import CookiesBanner from "../components/CookiesBanner";

import logoWhite from "../assets/applaby-white.png";
import mockupCenter from "../assets/barber/mockup-barber-center.png";
import mockupLeft from "../assets/barber/mockup-barber-left.png";
import mockupRight from "../assets/barber/mockup-barber-right.png";
import macBarber from "../assets/barber/mac-barber.png";
import barberPromoApp from "../assets/barber/barber-promo-app-display.png";
import notificacionBarber from "../assets/barber/notificacion-barber-1.png";
import notificacionBarber2 from "../assets/barber/notificacion-barber-2.png";
import noise from "../assets/barber/noise-texture.png";
import barberbg from "../assets/barber/barberbg.jpeg";
import barberBg2 from "../assets/barber/barber-bg.jpg";
import iconBarber1 from "../assets/barber/icon-barber1.png";
import iconBarber2 from "../assets/barber/icon-barber2.png";
import iconBarber3 from "../assets/barber/icon-barber3.png";

const PRIMARY = "#43BD98";
const CTA_URL =
	"https://creator.applaby.com?onboarding=true&appType=Services&appSubtype=Barber";
const WHATSAPP_URL = "https://wa.me/34608916357";

const fadeUp = {
	hidden: { opacity: 0, y: 30 },
	visible: { opacity: 1, y: 0 },
};

const stagger = {
	visible: { transition: { staggerChildren: 0.12 } },
};

const asArray = (value, fallback = []) =>
	Array.isArray(value) ? value : fallback;

/* ───────────────────────────── NAV ───────────────────────────── */

function Nav() {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 40);
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	useEffect(() => {
		document.body.style.overflow = open ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [open]);

	const scrollTo = (id) => {
		setOpen(false);
		document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
	};

	const links = [
		{ label: t("barber2.nav.home"), id: "inicio" },
		{ label: t("barber2.nav.features"), id: "funcionalidades" },
		{ label: t("barber2.nav.examples"), id: "ejemplos" },
		{ label: t("barber2.nav.pricing"), id: "precios" },
		{ label: t("barber2.nav.contact"), id: "contacto" },
	];

	return (
		<header
			className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 font-[Poppins] ${
				open
					? "bg-transparent"
					: scrolled
						? "bg-black/90 backdrop-blur-md shadow-lg shadow-black/20"
						: "bg-transparent"
			}`}
		>
			<div
				className={`relative z-[60] max-w-7xl mx-auto px-5 lg:px-10 flex items-center justify-between h-16 lg:h-[72px] ${
					open ? "bg-[#0a0a0a]/border-b border-white/[0.06]" : ""
				}`}
			>
				<a
					href="/barberias"
					className="flex items-center"
					onClick={(e) => {
						e.preventDefault();
						setOpen(false);
						scrollTo("inicio");
					}}
				>
					<img src={logoWhite} alt="Applaby" className="h-8 lg:h-9" />
				</a>

				{/* Desktop */}
				<nav className="hidden lg:flex items-center gap-7">
					{links.map((l) => (
						<button
							key={l.id}
							onClick={() => scrollTo(l.id)}
							className="text-sm text-gray-300 hover:text-white transition font-medium"
						>
							{l.label}
						</button>
					))}
					<a
						href={CTA_URL}
						target="_blank"
						rel="noreferrer"
						className="ml-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:scale-105 transition-transform shadow-lg shadow-primary/20"
					>
						{t("barber2.nav.cta")}
					</a>
				</nav>

				{/* Mobile toggle */}
				<button
					type="button"
					onClick={() => setOpen(!open)}
					className="lg:hidden relative z-[60] flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white transition hover:border-primary/40 hover:bg-primary/10"
					aria-label={open ? "Cerrar menú" : "Abrir menú"}
					aria-expanded={open}
				>
					{open ? <X size={20} strokeWidth={1.75} /> : <MenuIcon size={20} strokeWidth={1.75} />}
				</button>
			</div>

			{/* Mobile menu — fullscreen */}
			<AnimatePresence>
				{open && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.25 }}
						className="lg:hidden fixed inset-0 z-50 flex flex-col bg-[#070707]"
					>
						{/* Atmosphere */}
						<div
							className="pointer-events-none absolute inset-0 opacity-[0.35]"
							style={{
								backgroundImage: `url(${noise})`,
								backgroundRepeat: "repeat",
								backgroundSize: "220px",
							}}
						/>
						<div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-primary/15 blur-[100px]" />
						<div className="pointer-events-none absolute bottom-20 -left-16 h-64 w-64 rounded-full bg-primary/10 blur-[90px]" />

						{/* Spacer for fixed header */}
						<div className="h-16 shrink-0" />

						<div className="relative z-10 flex min-h-0 flex-1 flex-col px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6">
							<p className="mb-8 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary/80">
								{t("barber2.hero.badge")}
							</p>

							<nav className="flex flex-1 flex-col gap-1">
								{links.map((l, i) => (
									<motion.button
										key={l.id}
										type="button"
										onClick={() => scrollTo(l.id)}
										initial={{ opacity: 0, x: -16 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: -8 }}
										transition={{ duration: 0.35, delay: 0.05 + i * 0.05 }}
										className="group flex items-center gap-4 border-b border-white/[0.07] py-4 text-left transition active:bg-white/[0.03]"
									>
										<span className="w-7 text-[11px] font-medium tabular-nums text-primary/70">
											{String(i + 1).padStart(2, "0")}
										</span>
										<span className="flex-1 text-[1.65rem] font-semibold leading-none tracking-tight text-white transition group-hover:text-primary">
											{l.label}
										</span>
										<ChevronRight
											size={18}
											className="text-white/20 transition group-hover:translate-x-0.5 group-hover:text-primary"
										/>
									</motion.button>
								))}
							</nav>

							<motion.div
								initial={{ opacity: 0, y: 16 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.4, delay: 0.28 }}
								className="mt-8 space-y-3"
							>
								<a
									href={CTA_URL}
									target="_blank"
									rel="noreferrer"
									className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 text-[15px] font-semibold text-white shadow-[0_12px_40px_rgba(67,189,152,0.28)] transition hover:brightness-110"
								>
									{t("barber2.nav.ctaMobile")}
									<ArrowRight size={18} />
								</a>
								<a
									href={WHATSAPP_URL}
									target="_blank"
									rel="noreferrer"
									className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/12 bg-white/[0.03] py-3.5 text-sm font-medium text-gray-300 transition hover:border-white/25 hover:text-white"
								>
									<MessageCircle size={18} className="text-primary" />
									{t("barber2.finalCta.ctaSecondary")}
								</a>
								<p className="pt-1 text-center text-[11px] text-gray-500">
									{t("barber2.hero.trust")}
								</p>
							</motion.div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>

			<style>{`[id]{scroll-margin-top:80px}`}</style>
		</header>
	);
}

/* ───────────────────────────── HERO ───────────────────────────── */

function Hero() {
	const { t } = useTranslation();
	const ref = useRef(null);
	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ["start start", "end start"],
	});
	const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
	const proof = asArray(t("barber2.hero.proof", { returnObjects: true }));

	return (
		<section
			ref={ref}
			id="inicio"
			className="relative min-h-screen flex items-center overflow-hidden text-white"
		>
			{/* BG */}
			<motion.div
				className="absolute inset-0"
				style={{
					backgroundImage: `url(${barberbg})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					y: bgY,
					filter: "brightness(0.45) contrast(1.1)",
					scale: 1.1,
				}}
			/>
			<div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#0b0b0b]" />
			<div
				className="absolute inset-0 opacity-30 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>

			{/* Content */}
			<div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16 pt-32 pb-20 md:pt-40 md:pb-28 w-full grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
				{/* Text */}
				<div className="text-center md:text-left">
					{/* Badge — segmenta al instante */}
					<motion.p
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5 }}
						className="inline-block bg-primary/15 text-primary border border-primary/25 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide mb-6"
					>
						{t("barber2.hero.badge")}
					</motion.p>

					{/* H1 — deseo + resultado concreto */}
					<motion.h1
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.7, delay: 0.1 }}
						className="text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold leading-[1.08] mb-5"
					>
						{t("barber2.hero.h1")}
						<br />
						<span className="text-primary">{t("barber2.hero.h1Highlight")}</span>
					</motion.h1>

					{/* Subtítulo — qué obtienes y por qué es diferente */}
					<motion.p
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="text-gray-400 text-base md:text-lg mb-7 max-w-[420px] mx-auto md:mx-0 leading-relaxed"
					>
						{t("barber2.hero.subtitle")}
					</motion.p>

					{/* Proof strip — reduce riesgo percibido */}
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.28 }}
						className="flex flex-wrap justify-center md:justify-start gap-x-5 gap-y-1.5 mb-8 text-sm text-gray-500"
					>
						{proof.map((item, i) => (
							<span key={item} className="contents">
								{i > 0 && <span className="text-gray-700">·</span>}
								<span>{item}</span>
							</span>
						))}
					</motion.div>

					{/* CTAs */}
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.35 }}
						className="flex flex-col sm:flex-row justify-center md:justify-start gap-3"
					>
						<a
							href={CTA_URL}
							target="_blank"
							rel="noreferrer"
							className="bg-primary text-white px-8 py-3.5 rounded-xl font-semibold shadow-lg shadow-primary/25 hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2"
						>
							{t("barber2.hero.ctaPrimary")} <ChevronRight size={18} />
						</a>
					</motion.div>

					{/* Trust micro-copy */}
					<motion.p
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.6, delay: 0.5 }}
						className="mt-4 text-xs text-gray-600"
					>
						{t("barber2.hero.trust")}
					</motion.p>
				</div>

				{/* Mockups */}
				<div className="relative w-full flex justify-center">
					{/* Mobile */}
					<div className="flex md:hidden items-end justify-center relative h-[340px] w-full max-w-sm">
						<motion.img
							src={mockupLeft}
							alt={t("barber2.hero.alts.bookings")}
							className="absolute left-1/2 bottom-0 w-44 h-auto drop-shadow-2xl -translate-x-[72%] z-10"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
						/>
						<motion.img
							src={mockupCenter}
							alt={t("barber2.hero.alts.main")}
							className="w-52 h-auto drop-shadow-2xl relative z-20"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 20 }}
							transition={{ duration: 0.6, delay: 0.15 }}
						/>
						<motion.img
							src={mockupRight}
							alt={t("barber2.hero.alts.profile")}
							className="absolute right-1/2 bottom-0 w-44 h-auto drop-shadow-2xl translate-x-[72%] z-10"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.3 }}
						/>
						{/* Notificación nueva reserva — derecha */}
						<motion.img
							src={notificacionBarber}
							alt={t("barber2.hero.alts.newBooking")}
							className="absolute z-40 w-[210px] right-1 left-auto top-1 pointer-events-none"
							initial={{ opacity: 0, y: -24, scale: 0.92 }}
							animate={{
								opacity: 1,
								y: [0, -5, 0],
								scale: 1,
							}}
							transition={{
								opacity: { duration: 0.5, delay: 0.9 },
								scale: { duration: 0.5, delay: 0.9, type: "spring", stiffness: 260, damping: 18 },
								y: {
									duration: 3.5,
									repeat: Infinity,
									ease: "easeInOut",
									delay: 1.4,
								},
							}}
						/>
						{/* Notificación modificada — izquierda */}
						<motion.img
							src={notificacionBarber2}
							alt={t("barber2.hero.alts.modifiedBooking")}
							className="absolute z-40 w-[210px] left-1 top-[42%] pointer-events-none"
							initial={{ opacity: 0, x: -20, scale: 0.92 }}
							animate={{
								opacity: 1,
								x: 0,
								y: [0, 4, 0],
								scale: 1,
							}}
							transition={{
								opacity: { duration: 0.5, delay: 1.35 },
								x: { duration: 0.5, delay: 1.35 },
								scale: { duration: 0.5, delay: 1.35, type: "spring", stiffness: 260, damping: 18 },
								y: {
									duration: 4,
									repeat: Infinity,
									ease: "easeInOut",
									delay: 1.9,
								},
							}}
						/>
					</div>

					{/* Desktop */}
					<div className="hidden md:flex items-end justify-center relative -space-x-32 lg:-space-x-40">
						<motion.img
							src={mockupLeft}
							alt={t("barber2.hero.alts.bookings")}
							className="w-44 lg:w-60 h-auto drop-shadow-2xl -rotate-6 z-20"
							initial={{ opacity: 0, x: -80, y: 30 }}
							animate={{
								opacity: 1,
								x: 0,
								y: [0, 6, 0],
								rotate: -6,
							}}
							transition={{
								opacity: { duration: 0.6, delay: 0.2 },
								x: { duration: 0.6, delay: 0.2 },
								y: {
									duration: 6,
									repeat: Infinity,
									ease: "easeInOut",
								},
							}}
						/>
						<motion.img
							src={mockupCenter}
							alt={t("barber2.hero.alts.main")}
							className="w-52 lg:w-72 h-auto drop-shadow-2xl z-30"
							initial={{ opacity: 0, y: 80 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, ease: "easeOut" }}
						/>
						<motion.img
							src={mockupRight}
							alt={t("barber2.hero.alts.profile")}
							className="w-44 lg:w-60 h-auto drop-shadow-2xl rotate-6 z-20"
							initial={{ opacity: 0, x: 80, y: 30 }}
							animate={{
								opacity: 1,
								x: 0,
								y: [0, 6, 0],
								rotate: 6,
							}}
							transition={{
								opacity: { duration: 0.6, delay: 0.4 },
								x: { duration: 0.6, delay: 0.4 },
								y: {
									duration: 6,
									repeat: Infinity,
									ease: "easeInOut",
								},
							}}
						/>
						{/* Notificación nueva — derecha / arriba */}
						<motion.img
							src={notificacionBarber}
							alt={t("barber2.hero.alts.newBooking")}
							className="absolute z-40 w-[190px] lg:w-[220px] -right-2 lg:-right-6 top-6 lg:top-10 pointer-events-none drop-shadow-xl"
							initial={{ opacity: 0, y: -28, scale: 0.9 }}
							animate={{
								opacity: 1,
								y: [0, -6, 0],
								scale: 1,
							}}
							transition={{
								opacity: { duration: 0.55, delay: 1 },
								scale: {
									duration: 0.55,
									delay: 1,
									type: "spring",
									stiffness: 240,
									damping: 16,
								},
								y: {
									duration: 3.8,
									repeat: Infinity,
									ease: "easeInOut",
									delay: 1.6,
								},
							}}
						/>
						{/* Notificación modificada — izquierda / media altura */}
						<motion.img
							src={notificacionBarber2}
							alt={t("barber2.hero.alts.modifiedBooking")}
							className="absolute z-40 w-[175px] lg:w-[200px] left-28 lg:left-32 top-[38%] lg:top-[42%] pointer-events-none drop-shadow-xl"
							initial={{ opacity: 0, x: -28, scale: 0.9 }}
							animate={{
								opacity: 1,
								x: 0,
								y: [0, 5, 0],
								scale: 1,
							}}
							transition={{
								opacity: { duration: 0.55, delay: 1.45 },
								x: { duration: 0.55, delay: 1.45 },
								scale: {
									duration: 0.55,
									delay: 1.45,
									type: "spring",
									stiffness: 240,
									damping: 16,
								},
								y: {
									duration: 4.2,
									repeat: Infinity,
									ease: "easeInOut",
									delay: 2,
								},
							}}
						/>
					</div>
				</div>
			</div>

			<div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-b from-transparent to-[#0b0b0b]" />
		</section>
	);
}

/* ──────────────────── PROBLEM STRIP ──────────────────── */

function ProblemStrip() {
	const { t } = useTranslation();
	const icons = [Globe, Smartphone, Calendar, ShoppingBag, Gift, Bell];
	const items = asArray(
		t("barber2.pillars.items", { returnObjects: true })
	).map((item, i) => ({
		...item,
		icon: icons[i] || Globe,
	}));

	return (
		<section className="relative py-20 md:py-28 bg-[#0b0b0b] overflow-hidden">
			<div
				className="absolute inset-0 opacity-20 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>
			<div className="relative z-10 max-w-6xl mx-auto px-6">
				<motion.p
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="text-center text-primary uppercase tracking-wider text-sm font-semibold mb-3"
				>
					{t("barber2.pillars.eyebrow")}
				</motion.p>
				<motion.h2
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6, delay: 0.1 }}
					className="text-center text-white text-2xl md:text-4xl font-extrabold mb-4"
				>
					{t("barber2.pillars.title")}{" "}
					<br className="hidden sm:block" />
					<span className="text-primary">{t("barber2.pillars.titleHighlight")}</span>
				</motion.h2>
				<motion.p
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6, delay: 0.15 }}
					className="text-center text-gray-500 mb-14 max-w-lg mx-auto"
				>
					{t("barber2.pillars.subtitle")}
				</motion.p>

				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
					{items.map((item, i) => (
						<motion.div
							key={i}
							variants={fadeUp}
							initial="hidden"
							whileInView="visible"
							viewport={{ once: true }}
							transition={{ duration: 0.5, delay: 0.1 + i * 0.07 }}
							className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 lg:p-7 hover:border-primary/30 transition-colors"
						>
							<item.icon className="w-6 h-6 text-primary mb-3" />
							<p className="text-white font-semibold text-sm mb-1.5">{item.title}</p>
							<p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
}

/* ──────────────────── WEB SHOWCASE ──────────────────── */

function WebShowcase() {
	const { t } = useTranslation();
	const points = asArray(t("barber2.web.points", { returnObjects: true }));

	return (
		<section
			id="funcionalidades"
			className="relative py-20 md:py-28 bg-[#0b0b0b] overflow-hidden"
		>
			<div
				className="absolute inset-0 opacity-20 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>
			<div className="relative z-10 max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
				{/* Text */}
				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.7 }}
				>
					<p className="text-primary uppercase tracking-wider text-sm font-semibold mb-3">
						{t("barber2.web.eyebrow")}
					</p>
					<h2 className="text-white text-2xl md:text-4xl font-extrabold mb-4 leading-tight">
						{t("barber2.web.title")}
						<br />
						<span className="text-primary">{t("barber2.web.titleHighlight")}</span>
					</h2>
					<p className="text-gray-400 mb-8 leading-relaxed">
						{t("barber2.web.body")}
					</p>

					<div className="space-y-3 mb-8">
						{points.map((p, i) => (
							<div key={i} className="flex items-start gap-3">
								<CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
								<span className="text-gray-300 text-sm">
									{p}
								</span>
							</div>
						))}
					</div>

					<a
						href={CTA_URL}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3 rounded-xl font-semibold hover:scale-105 transition-transform shadow-lg shadow-primary/20"
					>
						{t("barber2.web.cta")} <ArrowRight size={18} />
					</a>
				</motion.div>

				{/* Mockup Mac — web */}
				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.7, delay: 0.2 }}
					className="flex justify-center"
				>
					<div className="relative w-full max-w-lg lg:max-w-xl">
						<div className="absolute -inset-4 bg-primary/10 rounded-3xl blur-2xl" />
						<img
							src={macBarber}
							alt={t("barber2.web.alt")}
							className="relative w-full h-auto drop-shadow-2xl"
						/>
					</div>
				</motion.div>
			</div>
		</section>
	);
}

/* ──────────────────── BOOKING SHOWCASE ──────────────────── */

function BookingShowcase() {
	const { t } = useTranslation();
	const steps = asArray(
		t("barber2.booking.steps", { returnObjects: true })
	).map((step, i) => ({
		...step,
		num: String(i + 1),
	}));

	return (
		<section className="relative py-20 md:py-28 bg-[#0f0f0f] overflow-hidden">
			<div
				className="absolute inset-0 opacity-20 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>
			<div className="relative z-10 max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
				{/* Mockup */}
				<motion.div
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 0.7 }}
					className="flex justify-center order-2 md:order-1"
				>
					<div className="relative">
						<div
							className="absolute -inset-6 rounded-3xl pointer-events-none"
							style={{
								background:
									"radial-gradient(ellipse at center, rgba(67,189,152,0.18) 0%, transparent 70%)",
							}}
						/>
						<img
							src={barberPromoApp}
							alt={t("barber2.booking.alt")}
							width={1400}
							height={1262}
							decoding="async"
							className="relative w-[min(100%,22rem)] sm:w-96 lg:w-[28rem] h-auto"
						/>
					</div>
				</motion.div>

				{/* Text */}
				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.7, delay: 0.15 }}
					className="order-1 md:order-2"
				>
					<p className="text-primary uppercase tracking-wider text-sm font-semibold mb-3">
						{t("barber2.booking.eyebrow")}
					</p>
					<h2 className="text-white text-2xl md:text-4xl font-extrabold mb-4 leading-tight">
						{t("barber2.booking.title")}
						<br />
						<span className="text-primary">
							{t("barber2.booking.titleHighlight")}
						</span>
					</h2>
					<p className="text-gray-400 mb-8 leading-relaxed">
						{t("barber2.booking.body")}
					</p>

					<div className="space-y-5">
						{steps.map((s) => (
							<div
								key={s.num}
								className="flex items-start gap-4"
							>
								<span className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-bold">
									{s.num}
								</span>
								<div>
									<p className="text-white font-semibold text-sm">
										{s.title}
									</p>
									<p className="text-gray-500 text-sm">
										{s.desc}
									</p>
								</div>
							</div>
						))}
					</div>
				</motion.div>
			</div>
		</section>
	);
}

/* ──────────────────── LOYALTY SHOWCASE ──────────────────── */

function LoyaltyShowcase() {
	const { t } = useTranslation();
	const icons = [Star, Gift, Zap];
	const cards = asArray(
		t("barber2.loyalty.items", { returnObjects: true })
	).map((card, i) => ({
		...card,
		icon: icons[i] || Star,
	}));

	return (
		<section className="relative py-20 md:py-28 bg-[#0b0b0b] overflow-hidden">
			<div
				className="absolute inset-0 opacity-20 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>
			<div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
				>
					<p className="text-primary uppercase tracking-wider text-sm font-semibold mb-3">
						{t("barber2.loyalty.eyebrow")}
					</p>
					<h2 className="text-white text-2xl md:text-4xl font-extrabold mb-4">
						{t("barber2.loyalty.title")}{" "}
						<span className="text-primary">{t("barber2.loyalty.titleHighlight")}</span>
					</h2>
					<p className="text-gray-400 mb-14 max-w-lg mx-auto">
						{t("barber2.loyalty.subtitle")}
					</p>
				</motion.div>

				<motion.div
					variants={stagger}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					className="grid grid-cols-1 md:grid-cols-3 gap-6"
				>
					{cards.map((c, i) => (
						<motion.div
							key={i}
							variants={fadeUp}
							transition={{ duration: 0.5 }}
							className="bg-white/[0.04] border border-white/10 rounded-2xl p-7 text-left hover:border-primary/30 transition-colors"
						>
							<c.icon className="w-8 h-8 text-primary mb-4" />
							<h3 className="text-white font-bold text-lg mb-2">
								{c.title}
							</h3>
							<p className="text-gray-400 text-sm leading-relaxed">
								{c.desc}
							</p>
						</motion.div>
					))}
				</motion.div>
			</div>
		</section>
	);
}

/* ──────────────────── OWN BRAND vs MARKETPLACE ──────────────────── */

function OwnBrand() {
	const { t } = useTranslation();
	const icons = [Globe, Smartphone, Users, Shield];
	const yours = asArray(
		t("barber2.ownBrand.items", { returnObjects: true })
	).map((text, i) => ({
		text,
		icon: icons[i] || Globe,
	}));

	return (
		<section className="relative py-20 md:py-28 bg-[#0f0f0f] overflow-hidden">
			<div
				className="absolute inset-0 opacity-20 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>
			<div className="relative z-10 max-w-5xl mx-auto px-6">
				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="text-center mb-14"
				>
					<p className="text-primary uppercase tracking-wider text-sm font-semibold mb-3">
						{t("barber2.ownBrand.eyebrow")}
					</p>
					<h2 className="text-white text-2xl md:text-4xl font-extrabold mb-4">
						{t("barber2.ownBrand.title")}{" "}
						<span className="text-primary">{t("barber2.ownBrand.titleHighlight")}</span>
					</h2>
					<p className="text-gray-400 max-w-xl mx-auto">
						{t("barber2.ownBrand.subtitle")}
					</p>
				</motion.div>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
					{yours.map((item, i) => (
						<motion.div
							key={i}
							variants={fadeUp}
							initial="hidden"
							whileInView="visible"
							viewport={{ once: true }}
							transition={{ duration: 0.5, delay: 0.1 + i * 0.07 }}
							className="bg-white/[0.03] border border-primary/20 rounded-2xl p-6 flex items-start gap-3"
						>
							<item.icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
							<span className="text-gray-300 text-sm">
								{item.text}
							</span>
						</motion.div>
					))}
				</div>

				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6, delay: 0.35 }}
					className="text-center mt-12"
				>
					<a
						href={CTA_URL}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 rounded-xl font-semibold hover:scale-105 transition-transform shadow-lg shadow-primary/20"
					>
						{t("barber2.ownBrand.cta")} <ArrowRight size={18} />
					</a>
				</motion.div>
			</div>
		</section>
	);
}

/* ──────────────────── HOW IT WORKS ──────────────────── */

function HowItWorks() {
	const { t } = useTranslation();
	const icons = [Zap, Globe, Calendar];
	const steps = asArray(
		t("barber2.howItWorks.steps", { returnObjects: true })
	).map((step, i) => ({
		...step,
		num: String(i + 1),
		icon: icons[i] || Zap,
	}));

	return (
		<section className="relative py-20 md:py-28 bg-[#0b0b0b] overflow-hidden">
			<div
				className="absolute inset-0 opacity-20 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>
			<div className="relative z-10 max-w-5xl mx-auto px-6">
				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="text-center mb-14"
				>
					<p className="text-primary uppercase tracking-wider text-sm font-semibold mb-3">
						{t("barber2.howItWorks.eyebrow")}
					</p>
					<h2 className="text-white text-2xl md:text-4xl font-extrabold">
						{t("barber2.howItWorks.title")}{" "}
						<span className="text-primary">{t("barber2.howItWorks.titleHighlight")}</span>
					</h2>
				</motion.div>

				<motion.div
					variants={stagger}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					className="grid grid-cols-1 md:grid-cols-3 gap-6"
				>
					{steps.map((s, i) => (
						<motion.div
							key={i}
							variants={fadeUp}
							transition={{ duration: 0.5 }}
							className="relative bg-white/[0.04] border border-white/10 rounded-2xl p-7 text-left group hover:border-primary/30 transition-colors"
						>
							<div className="flex items-center gap-3 mb-4">
								<span className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
									{s.num}
								</span>
								<span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded-md">
									{s.time}
								</span>
							</div>
							<s.icon className="w-7 h-7 text-primary mb-3" />
							<h3 className="text-white font-bold text-lg mb-2">
								{s.title}
							</h3>
							<p className="text-gray-400 text-sm leading-relaxed">
								{s.desc}
							</p>
						</motion.div>
					))}
				</motion.div>
			</div>
		</section>
	);
}

/* ──────────────────── REAL EXAMPLES ──────────────────── */

const CARD_GAP = 16;

function Examples() {
	const { t } = useTranslation();
	const isMobile = useMediaQuery("(max-width: 640px)");
	const trackRef = useRef(null);
	const [active, setActive] = useState(0);
	const webAppTag = t("barber2.examples.webAppTag");
	const webTag = t("barber2.examples.webTag");

	const stepWidth = () => {
		const track = trackRef.current;
		const first = track?.firstElementChild;
		return first ? first.offsetWidth + CARD_GAP : 0;
	};

	const handleScroll = useCallback(() => {
		const track = trackRef.current;
		const step = stepWidth();
		if (!track || !step) return;
		setActive(Math.round(track.scrollLeft / step));
	}, []);

	const goTo = (index) => {
		const track = trackRef.current;
		if (!track) return;
		track.scrollTo({ left: index * stepWidth(), behavior: "smooth" });
	};

	const examples = [
		{
			name: "Elite Barber",
			tag: webAppTag,
			image: iconBarber1,
			web: "https://www.elitebarber.es/",
			ios: "https://apps.apple.com/us/app/elite-studio/id6742740084",
			android:
				"https://play.google.com/store/apps/details?id=com.applaby.elite",
		},
		{
			name: "Barbería Marco",
			tag: webAppTag,
			image: iconBarber2,
			web: "https://web.applaby.com/barberia-marco/",
			ios: "https://apps.apple.com/us/app/la-barber%C3%ADa-de-marco/id6745147346",
			android:
				"https://play.google.com/store/apps/details?id=com.applaby.barberiamarco",
		},
		{
			name: "Temple Fade Studio",
			tag: webTag,
			image: iconBarber3,
			web: "https://www.templefadestudio.es/",
		},
	];

	return (
		<section
			id="ejemplos"
			className="relative py-20 md:py-28 bg-[#0f0f0f] overflow-hidden"
		>
			<div
				className="absolute inset-0 opacity-20 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>
			<div className="relative z-10 max-w-5xl mx-auto px-6">
				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="text-center mb-12 md:mb-14"
				>
					<p className="text-primary uppercase tracking-wider text-sm font-semibold mb-3">
						{t("barber2.examples.eyebrow")}
					</p>
					<h2 className="text-white text-2xl md:text-4xl font-extrabold mb-3">
						{t("barber2.examples.title")}{" "}
						<span className="text-primary">{t("barber2.examples.titleHighlight")}</span>
					</h2>
					<p className="text-gray-500 max-w-md mx-auto">
						{t("barber2.examples.subtitle")}
					</p>
				</motion.div>

				<div
					ref={trackRef}
					onScroll={isMobile ? handleScroll : undefined}
					className={
						isMobile
							? "hide-scrollbar"
							: "grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5"
					}
					style={
						isMobile
							? {
									display: "flex",
									gap: `${CARD_GAP}px`,
									overflowX: "auto",
									scrollSnapType: "x mandatory",
									marginInline: "-1.5rem",
									paddingInline: "1.5rem",
									scrollPaddingInline: "1.5rem",
									WebkitOverflowScrolling: "touch",
							  }
							: undefined
					}
				>
					{examples.map((ex, i) => (
						<motion.div
							key={i}
							variants={isMobile ? undefined : fadeUp}
							initial={isMobile ? { opacity: 0, y: 20 } : "hidden"}
							animate={isMobile ? { opacity: 1, y: 0 } : undefined}
							whileInView={isMobile ? undefined : "visible"}
							viewport={{ once: true }}
							transition={{ duration: 0.5, delay: i * 0.1 }}
							className="group flex flex-col bg-[#141414] border border-white/10 rounded-2xl overflow-hidden hover:border-primary/40 transition-colors duration-300"
							style={
								isMobile
									? { flex: "0 0 78%", scrollSnapAlign: "start" }
									: undefined
							}
						>
							<a
								href={ex.web}
								target="_blank"
								rel="noreferrer"
								className="relative h-40 md:h-44 bg-[#0a0a0a] flex items-center justify-center p-8 border-b border-white/5"
							>
								<img
									src={ex.image}
									alt={ex.name}
									className="max-h-full max-w-full object-contain opacity-90 group-hover:opacity-100 transition-opacity"
								/>
								<span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider font-semibold text-primary/90 bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-md">
									{t("barber2.examples.demoTag")}
								</span>
							</a>

							<div className="flex flex-col flex-1 p-5">
								<div className="mb-5">
									<p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">
										{ex.tag}
									</p>
									<h3 className="text-white font-semibold text-base">
										{ex.name}
									</h3>
								</div>

								<div className="mt-auto space-y-3">
									<a
										href={ex.web}
										target="_blank"
										rel="noreferrer"
										className="inline-flex w-full items-center justify-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors"
									>
										{t("barber2.examples.tryDemo")} <ArrowRight size={16} />
									</a>

									<div className="flex items-center justify-center gap-3 text-xs text-gray-500 min-h-[1.25rem]">
										{ex.ios && (
											<a
												href={ex.ios}
												target="_blank"
												rel="noreferrer"
												className="hover:text-primary transition-colors"
											>
												{t("barber2.examples.ios")}
											</a>
										)}
										{ex.ios && ex.android && (
											<span className="text-gray-700">·</span>
										)}
										{ex.android && (
											<a
												href={ex.android}
												target="_blank"
												rel="noreferrer"
												className="hover:text-primary transition-colors"
											>
												{t("barber2.examples.android")}
											</a>
										)}
									</div>
								</div>
							</div>
						</motion.div>
					))}
				</div>

				{isMobile && (
					<div className="flex justify-center gap-2 mt-5">
						{examples.map((ex, index) => (
							<button
								key={ex.name}
								type="button"
								onClick={() => goTo(index)}
								aria-label={ex.name}
								aria-current={index === active}
								className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0"
							>
								<span
									className="block h-2 rounded"
									style={{
										width: index === active ? "24px" : "8px",
										background: index === active ? PRIMARY : "#374151",
										transition: "width 0.25s ease, background 0.25s ease",
									}}
								/>
							</button>
						))}
					</div>
				)}
			</div>
		</section>
	);
}

/* ──────────────────── PRICING ──────────────────── */

function Pricing() {
	const { t } = useTranslation();

	const planFeatures = asArray(
		t("barber2.pricing.plan.features", { returnObjects: true })
	);
	const planProFeatures = asArray(
		t("barber2.pricing.planPro.features", { returnObjects: true })
	);

	const plans = [
		{
			id: "complete",
			title: t("barber2.pricing.plan.name"),
			price: t("barber2.pricing.plan.price"),
			period: t("barber2.pricing.plan.period"),
			note: t("barber2.pricing.plan.note"),
			badge: t("barber2.pricing.plan.badge"),
			highlighted: true,
			features: planFeatures,
			cta: t("barber2.pricing.plan.cta"),
			trial: t("barber2.pricing.plan.trial"),
		},
		{
			id: "shop",
			title: t("barber2.pricing.planPro.name"),
			price: t("barber2.pricing.planPro.price"),
			period: t("barber2.pricing.planPro.period"),
			note: t("barber2.pricing.planPro.note"),
			badge: t("barber2.pricing.planPro.badge"),
			highlighted: false,
			features: planProFeatures,
			cta: t("barber2.pricing.planPro.cta"),
			trial: t("barber2.pricing.planPro.trial"),
		},
	];

	return (
		<section
			id="precios"
			className="relative py-20 md:py-28 bg-[#0b0b0b] overflow-hidden font-[Poppins]"
		>
			<div
				className="absolute inset-0 opacity-20 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>
			<div className="relative z-10 max-w-5xl mx-auto px-6">
				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="text-center mb-14"
				>
					<p className="text-primary uppercase tracking-wider text-sm font-semibold mb-3">
						{t("barber2.pricing.eyebrow")}
					</p>
					<h2 className="text-white text-2xl md:text-4xl font-extrabold mb-3">
						{t("barber2.pricing.title")}
					</h2>
					<p className="text-gray-500 max-w-md mx-auto">
						{t("barber2.pricing.subtitle")}
					</p>
				</motion.div>

				<div className="flex flex-wrap justify-center gap-8">
					{plans.map((plan, i) => (
						<motion.div
							key={plan.id}
							variants={fadeUp}
							initial="hidden"
							whileInView="visible"
							viewport={{ once: true }}
							transition={{ duration: 0.6, delay: 0.1 + i * 0.1 }}
							className={`relative w-full max-w-[400px] bg-[#141414] rounded-2xl p-8 shadow-xl shadow-black/30 ${
								plan.highlighted
									? "border-2 border-primary/40"
									: "border border-white/10"
							}`}
						>
							{plan.highlighted && plan.badge && (
								<div
									className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 z-10"
									style={{
										backgroundColor: PRIMARY,
										color: "#000",
									}}
								>
									<Sparkles className="w-4 h-4" /> {plan.badge}
								</div>
							)}

							{!plan.highlighted && plan.badge && (
								<span className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 bg-primary/15 text-primary">
									{plan.badge}
								</span>
							)}

							<h3 className="text-white font-bold text-xl mb-1">
								{plan.title}
							</h3>

							<div className="mb-5 mt-4">
								<div className="flex items-end gap-1">
									<span className="text-5xl font-extrabold text-primary leading-none">
										{plan.price}
									</span>
									<span className="text-gray-500 text-sm mb-1">
										{plan.period}
									</span>
								</div>
								<p className="text-gray-500 text-xs mt-2">{plan.note}</p>
							</div>

							<ul className="space-y-2.5 mb-7">
								{plan.features.map((f, idx) => (
									<li
										key={idx}
										className="flex items-start gap-2.5 text-sm text-gray-300"
									>
										<CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
										{f}
									</li>
								))}
							</ul>

							<a
								href={CTA_URL}
								target="_blank"
								rel="noreferrer"
								className="block w-full text-center bg-primary text-white py-3.5 rounded-xl font-semibold text-base shadow-lg shadow-primary/25 hover:scale-[1.02] transition-transform"
							>
								{plan.cta}
							</a>
							{plan.trial && (
								<p className="text-center text-gray-600 text-xs mt-3">
									{plan.trial}
								</p>
							)}
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
}

/* ──────────────────── FAQ ──────────────────── */

function FAQ() {
	const { t } = useTranslation();
	const [openIdx, setOpenIdx] = useState(null);
	const items = asArray(t("barber2.faq.items", { returnObjects: true }));

	return (
		<section className="relative py-20 md:py-28 bg-[#0f0f0f] overflow-hidden font-[Poppins]">
			<div
				className="absolute inset-0 opacity-20 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>
			<div className="relative z-10 max-w-3xl mx-auto px-6">
				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="text-center mb-12"
				>
					<p className="text-primary uppercase tracking-wider text-sm font-semibold mb-3">
						{t("barber2.faq.eyebrow")}
					</p>
					<h2 className="text-white text-2xl md:text-4xl font-extrabold">
						{t("barber2.faq.title")}
					</h2>
				</motion.div>

				<div className="space-y-3">
					{items.map((item, i) => (
						<motion.div
							key={i}
							variants={fadeUp}
							initial="hidden"
							whileInView="visible"
							viewport={{ once: true }}
							transition={{ duration: 0.4, delay: i * 0.05 }}
							className="border border-white/10 rounded-xl overflow-hidden"
						>
							<button
								onClick={() =>
									setOpenIdx(openIdx === i ? null : i)
								}
								className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.03] transition-colors"
							>
								<span className="font-medium text-white pr-4 text-sm">
									{item.q}
								</span>
								<ChevronDown
									className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-300 ${
										openIdx === i ? "rotate-180" : ""
									}`}
								/>
							</button>
							<AnimatePresence>
								{openIdx === i && (
									<motion.div
										initial={{
											height: 0,
											opacity: 0,
										}}
										animate={{
											height: "auto",
											opacity: 1,
										}}
										exit={{
											height: 0,
											opacity: 0,
										}}
										transition={{ duration: 0.3 }}
										className="overflow-hidden"
									>
										<div className="px-5 pb-5 text-gray-400 text-sm leading-relaxed border-t border-white/5 pt-4">
											{item.a}
										</div>
									</motion.div>
								)}
							</AnimatePresence>
						</motion.div>
					))}
				</div>

				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6, delay: 0.3 }}
					className="mt-10 text-center p-6 rounded-2xl bg-white/[0.03] border border-white/10"
				>
					<p className="text-gray-400 mb-4">
						{t("barber2.faq.moreQuestion")}
					</p>
					<a
						href={WHATSAPP_URL}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-transform hover:scale-105"
						style={{ backgroundColor: "#25D366" }}
					>
						<MessageCircle className="w-5 h-5" />
						{t("barber2.faq.whatsapp")}
					</a>
				</motion.div>
			</div>
		</section>
	);
}

/* ──────────────────── FINAL CTA ──────────────────── */

function FinalCTA() {
	const { t } = useTranslation();

	return (
		<section
			id="contacto"
			className="relative py-24 md:py-32 text-center text-white overflow-hidden"
			style={{
				backgroundImage: `url(${barberBg2})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
			}}
		>
			<div className="absolute inset-0 bg-black/60" />
			<div
				className="absolute inset-0 opacity-20 pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px",
				}}
			/>

			<div className="relative z-10 max-w-2xl mx-auto px-6">
				<motion.p
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="text-primary uppercase tracking-wider text-sm font-semibold mb-4"
				>
					{t("barber2.finalCta.eyebrow")}
				</motion.p>
				<motion.h2
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.7, delay: 0.1 }}
					className="text-3xl md:text-5xl font-extrabold mb-5 leading-tight"
				>
					{t("barber2.finalCta.title1")}
					<br />
					<span className="text-primary">
						{t("barber2.finalCta.titleHighlight")}
					</span>
				</motion.h2>

				<motion.div
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.6, delay: 0.25 }}
					className="flex flex-col sm:flex-row justify-center gap-4 mt-8"
				>
					<a
						href={CTA_URL}
						target="_blank"
						rel="noreferrer"
						className="bg-primary text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg shadow-primary/25 hover:scale-105 transition-all flex items-center justify-center gap-2"
					>
						{t("barber2.finalCta.ctaPrimary")} <ChevronRight size={20} />
					</a>
					<a
						href={WHATSAPP_URL}
						target="_blank"
						rel="noreferrer"
						className="border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition flex items-center justify-center gap-2"
					>
						<MessageCircle size={20} /> {t("barber2.finalCta.ctaSecondary")}
					</a>
				</motion.div>

				<motion.p
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					transition={{ duration: 0.5, delay: 0.35 }}
					className="mt-5 text-sm text-gray-400"
				>
					{t("barber2.finalCta.trust")}
				</motion.p>
			</div>
		</section>
	);
}

/* ──────────────────── FOOTER ──────────────────── */

function Footer() {
	const { t, i18n } = useTranslation();

	const changeLanguage = (lng) => {
		i18n.changeLanguage(lng);
	};

	const langBtn = (lng, label) => (
		<button
			onClick={() => changeLanguage(lng)}
			className={`px-2 py-1 rounded text-xs transition ${
				i18n.language?.startsWith(lng)
					? "bg-primary text-white"
					: "text-gray-500 hover:text-white"
			}`}
		>
			{label}
		</button>
	);

	return (
		<footer className="bg-[#0a0a0a] text-gray-400 font-[Poppins]">
			<div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
				<div>
					<img src={logoWhite} alt="Applaby" className="h-9 mb-4" />
					<p className="text-sm text-gray-500">
						{t("barber2.footer.claim")}
					</p>
				</div>

				<div className="flex flex-col gap-2 text-sm">
					<a
						href="/"
						className="hover:text-primary transition"
					>
						{t("barber2.footer.home")}
					</a>
					<a
						href="https://applaby.com/terminos"
						target="_blank"
						rel="noreferrer"
						className="hover:text-primary transition"
					>
						{t("barber2.footer.terms")}
					</a>
					<a
						href="https://applaby.com/politica-privacidad"
						target="_blank"
						rel="noreferrer"
						className="hover:text-primary transition"
					>
						{t("barber2.footer.privacy")}
					</a>
				</div>

				<div className="flex flex-col gap-3 text-sm">
					<a
						href="mailto:info@applaby.com"
						className="flex items-center gap-2 hover:text-primary transition"
					>
						<Mail className="w-4 h-4" /> info@applaby.com
					</a>
					<a
						href="tel:+34608916357"
						className="flex items-center gap-2 hover:text-primary transition"
					>
						<Phone className="w-4 h-4" /> +34 608 916 357
					</a>
					<a
						href="https://www.instagram.com/applaby_es"
						target="_blank"
						rel="noreferrer"
						className="flex items-center gap-2 hover:text-primary transition"
					>
						<Instagram className="w-5 h-5" /> @applaby_es
					</a>
				</div>
			</div>

			<div className="border-t border-white/5 py-5 text-xs text-gray-600 flex flex-col sm:flex-row items-center justify-center gap-4">
				<span>
					© {new Date().getFullYear()} Applaby. {t("barber2.footer.rights")}
				</span>
				<div className="flex items-center gap-2">
					<Globe className="w-4 h-4 text-gray-500" />
					<div className="flex gap-1">
						{langBtn("es", t("barber2.footer.langEs"))}
						{langBtn("en", t("barber2.footer.langEn"))}
						{langBtn("ca", t("barber2.footer.langCa"))}
					</div>
				</div>
			</div>
		</footer>
	);
}

/* ═══════════════════════════════════ PAGE ═══════════════════════════════════ */

export default function Barber2() {
	const { t } = useTranslation();

	useEffect(() => {
		document.documentElement.classList.add("barber-bg");
		document.body.classList.add("barber-bg");
		return () => {
			document.documentElement.classList.remove("barber-bg");
			document.body.classList.remove("barber-bg");
		};
	}, []);

	return (
		<>
			<Helmet>
				<title>{t("barber2.seo.title")}</title>
				<meta
					name="description"
					content={t("barber2.seo.description")}
				/>
				<meta
					name="keywords"
					content={t("barber2.seo.keywords")}
				/>
				<link
					rel="canonical"
					href="https://applaby.com/barberias"
				/>

				<meta
					property="og:title"
					content={t("barber2.seo.ogTitle")}
				/>
				<meta
					property="og:description"
					content={t("barber2.seo.ogDescription")}
				/>
				<meta
					property="og:url"
					content="https://applaby.com/barberias"
				/>
				<meta property="og:type" content="website" />

				<meta
					name="twitter:card"
					content="summary_large_image"
				/>
				<meta
					name="twitter:title"
					content={t("barber2.seo.ogTitle")}
				/>
				<meta
					name="twitter:description"
					content={t("barber2.seo.ogDescription")}
				/>
			</Helmet>

			<Nav />
			<Hero />
			<ProblemStrip />
			<WebShowcase />
			<BookingShowcase />
			<LoyaltyShowcase />
			<OwnBrand />
			<HowItWorks />
			<Examples />
			<Pricing />
			<FAQ />
			<FinalCTA />
			<Footer />
			<CookiesBanner />
		</>
	);
}
